import React, { useMemo } from 'react';
import { Inventory } from '../../types';
import { ChevronLeft, ChevronRight, Package, User } from 'lucide-react';
import { ArmyTemplate } from '../../types';
import { calculateCost, DRAFT_COSTS } from '../../utils/gameRules';

interface StarterPackProps {
  onSelect: (pack: Inventory) => void;
  customTemplates?: ArmyTemplate[];
  maxBudget: number;
}

const BASE_PACKS = [
  {
    name: 'Standard Legion',
    baseDesc: 'A balanced force.',
    baseInventory: { p: 128, n: 24, b: 24, r: 32, q: 1, k: 1 } // Base Standard Army
  },
  {
    name: 'The Horde',
    baseDesc: 'Overwhelming numbers.',
    baseInventory: { p: 64, n: 2, b: 2, r: 2, q: 1, k: 1 } // Base ~100 cost ratio
  },
  {
    name: 'Royal Guard',
    baseDesc: 'Elite heavy.',
    baseInventory: { p: 24, n: 6, b: 6, r: 6, q: 1, k: 1 } // Base ~100 cost ratio
  }
];

export const StarterPack: React.FC<StarterPackProps> = ({ onSelect, customTemplates = [], maxBudget }) => {
  const [startIndex, setStartIndex] = React.useState(0);

  // Memoize the scaled packs so they don't jitter
  const scaledPacks = useMemo(() => {
    return BASE_PACKS.map(pack => {
      // 1. Calculate Base Cost of the template
      const baseCost = calculateCost(pack.baseInventory);

      // 2. Determine Scale Factor (Target ~95% of max budget to be safe, or 100%)
      const targetBudget = maxBudget * 0.98;
      const scale = targetBudget / baseCost;

      // 3. Apply Scale
      const newInventory = { ...pack.baseInventory };

      // Scale pieces (keep 1 K, 1 Q fixed usually, but if budget is tiny, Q might be expensive? 
      // Assuming Q(9) is affordable in all brackets > 100).

      // We scale P, N, B, R
      newInventory.p = Math.max(8, Math.floor(pack.baseInventory.p * scale)); // Min 8 Pawns rule
      newInventory.n = Math.floor(pack.baseInventory.n * scale);
      newInventory.b = Math.floor(pack.baseInventory.b * scale);
      newInventory.r = Math.floor(pack.baseInventory.r * scale);

      // Recalculate cost to see if we fit/have extra space
      let currentCost = calculateCost(newInventory);

      // If we went over (due to min pawns), trim
      while (currentCost > maxBudget && newInventory.r > 0) { newInventory.r--; currentCost = calculateCost(newInventory); }
      while (currentCost > maxBudget && newInventory.n > 0) { newInventory.n--; currentCost = calculateCost(newInventory); }

      // If we are under, fill with pawns
      if (currentCost < maxBudget) {
        const diff = maxBudget - currentCost;
        const pawnsToAdd = Math.floor(diff / DRAFT_COSTS.p);
        newInventory.p += pawnsToAdd;
      }

      const desc = `${pack.baseDesc} (${newInventory.p}P, ${newInventory.n}N, ${newInventory.b}B, ${newInventory.r}R)`;

      return {
        id: pack.name,
        name: pack.name,
        description: desc,
        inventory: newInventory,
        isCustom: false
      };
    });
  }, [maxBudget]);

  const customPacks = customTemplates.map(t => ({
    ...t,
    isCustom: true
  }));

  const allPacks = [...customPacks, ...scaledPacks];
  const VISIBLE_COUNT = 3;

  const handleNext = () => {
    if (startIndex + VISIBLE_COUNT < allPacks.length) {
      setStartIndex(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (startIndex > 0) {
      setStartIndex(prev => prev - 1);
    }
  };

  const visiblePacks = allPacks.slice(startIndex, startIndex + VISIBLE_COUNT);

  return (
    <div className="relative mb-8 group/carousel">
      {/* Controls */}
      {startIndex > 0 && (
        <button
          onClick={handlePrev}
          className="absolute -left-4 top-1/2 -translate-y-1/2 z-10 bg-zinc-900 border border-zinc-700 p-2 rounded-full text-tudor-gold hover:bg-zinc-800 transition-all shadow-lg"
        >
          <ChevronLeft size={20} />
        </button>
      )}

      {startIndex + VISIBLE_COUNT < allPacks.length && (
        <button
          onClick={handleNext}
          className="absolute -right-4 top-1/2 -translate-y-1/2 z-10 bg-zinc-900 border border-zinc-700 p-2 rounded-full text-tudor-gold hover:bg-zinc-800 transition-all shadow-lg"
        >
          <ChevronRight size={20} />
        </button>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {visiblePacks.map((pack) => (
          <button
            key={pack.id}
            onClick={() => onSelect(pack.inventory)}
            className={`
                border p-4 rounded text-left transition-all group relative overflow-hidden
                ${pack.isCustom
                ? 'border-tudor-gold/30 bg-zinc-900 hover:border-tudor-gold hover:bg-zinc-900/80'
                : 'border-tudor-gray bg-tudor-dark hover:border-tudor-gold hover:bg-gray-900'
              }
            `}
          >
            {pack.isCustom && (
              <div className="absolute top-2 right-2 text-tudor-gold/20 group-hover:text-tudor-gold/40">
                <User size={64} strokeWidth={1} />
              </div>
            )}

            <div className="relative z-10">
              <div className="flex items-center mb-2 text-tudor-gold">
                {pack.isCustom ? <User size={18} className="mr-2" /> : <Package size={18} className="mr-2" />}
                <span className="font-bold uppercase tracking-wider truncate">{pack.name}</span>
              </div>
              <p className="text-sm text-gray-400 group-hover:text-gray-200 line-clamp-2 min-h-[2.5em]">{pack.description}</p>
              {pack.isCustom && (
                <div className="mt-2 text-[10px] text-zinc-600 uppercase tracking-widest">Custom Template</div>
              )}
            </div>
          </button>
        ))}
        {/* Placeholder if fewer than 3 */}
        {visiblePacks.length < 3 && Array.from({ length: 3 - visiblePacks.length }).map((_, i) => (
          <div key={`placeholder-${i}`} className="border border-dashed border-zinc-800 rounded bg-transparent opacity-50"></div>
        ))}
      </div>
    </div>
  );
};
